import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-lg px-2.5 py-0.5 text-xs font-bold tracking-tight transition-all duration-200 focus:outline-none",
  {
    variants: {
      variant: {
        default: "bg-accent text-accent-foreground",
        primary: "bg-primary text-primary-foreground shadow-sm",
        secondary: "border border-border bg-secondary text-secondary-foreground",
        success: "bg-brand-emerald-100 text-brand-emerald-700 border border-brand-emerald-200",
        warning: "bg-brand-amber-100 text-brand-amber-700 border border-brand-amber-200",
        destructive: "bg-brand-rose-100 text-brand-rose-600 border border-brand-rose-200",
        info: "bg-sky-100 text-sky-700 border border-sky-200",
        outline: "border border-border text-foreground",
        ghost: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "text-xs px-2.5 py-0.5",
        sm: "text-[10px] px-2 py-0.5",
        lg: "text-sm px-3 py-1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean
}

function Badge({ className, variant, size, dot, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {dot && (
        <span className={cn(
          "mr-1.5 h-1.5 w-1.5 rounded-full",
          variant === "default" && "bg-primary",
          variant === "primary" && "bg-primary-foreground/80",
          variant === "secondary" && "bg-secondary-foreground/70",
          variant === "success" && "bg-brand-emerald-500",
          variant === "warning" && "bg-brand-amber-500",
          variant === "destructive" && "bg-brand-rose-500",
          variant === "info" && "bg-sky-500"
        )} />
      )}
    </div>
  )
}

export { Badge, badgeVariants }
